// @givmo/employer-module — the ONE place a company app's slug becomes the name a person reads
// (EP-SUITE8, D-EP-69).
//
// Both hosts name apps from here: the employer portal's join requests and Admins page, and the
// company console, whose `company/products.ts` re-exports `productLabel` (Team access, the app
// switches, the audit trail and the Join requests page). Three maps used to disagree — the portal's
// join requests still called Donations "Employer portal" — and the pickers that grant apps
// (`GET /admin/company/module-choices`) carry no label, so the name has to come from one place.
//
// The keys are the backend's company-class catalog slugs, every one `module-choices` can serve
// (`app/domain/org_module.py` `ORG_MODULES_BY_ENTITY_CLASS`), in its order. `employer` is the
// company's Donations app (D-EP-48 ruling 1, Jonathan 2026-09-29: "Donations"; the slug stays
// `employer`).
//
// ⚠ IT IS NOT THE CONSOLE'S MODULE REGISTRY. `apps/company/src/modules/module-registry.ts` names the
// app-menu TILES, keyed by the web's own slugs, and two of its tiles read differently ("Business",
// "Sales"); this map names an app wherever someone is given it or asks for it.
//
// UNKNOWN SLUGS ARE TITLEIZED, never printed raw and never blanked: an app the backend adds before
// this map knows it still reads as a name a person can act on.

/** Each company app's name, by its catalog slug. */
const APP_NAMES: Readonly<Record<string, string>> = {
  employer: 'Donations',
  campaigns: 'Campaigns',
  sales_gifting: 'Sales gifting',
  books: 'Books',
  fundraisers: 'Fundraisers',
  compliance: 'Compliance',
  board: 'Board',
  projects: 'Projects',
};

/**
 * The name of the app a slug names.
 *
 * An empty or absent slug renders as an em dash (there is nothing to name); an unknown one is
 * titleized from the slug itself (`brand_match` → "Brand match"), and one with nothing but
 * separators comes back as it was.
 */
export function productLabel(product: string | null | undefined): string {
  const slug = (product ?? '').trim();
  if (slug.length === 0) return '—';
  const known = APP_NAMES[slug];
  if (known !== undefined) return known;
  const words = slug.replace(/[_-]+/g, ' ').trim();
  if (words.length === 0) return slug;
  return words.charAt(0).toUpperCase() + words.slice(1);
}
